import { apiService } from "./api.service";
import { PaginatedResponse } from "types/index";
import { Company } from "types/company";

class CompaniesService {
  async getAll(): Promise<Company[]> {
    const response = await apiService.get<any>(`/company/GetAll`, {
      // Do not trigger global loader when fetching companies for license modal
      // to avoid blocking UI with a page-level spinner.
      metadata: { skipGlobalLoading: true },
    } as any);

    const payload: any = (response as any)?.data ?? response;
    if (Array.isArray(payload)) {
      return payload as Company[];
    }
    if (Array.isArray((payload as any)?.data)) {
      return (payload as any).data as Company[];
    }
    if (Array.isArray((response as any)?.items)) {
      return (response as any).items as Company[];
    }
    throw new Error((response as any)?.message || "Failed to load companies");
  }

  async list(
    page: number = 1,
    limit: number = 100
  ): Promise<PaginatedResponse<Company>> {
    const response = await apiService.get<PaginatedResponse<Company>>(
      `/company/GetPageList?page=${page}&pageSize=${limit}`
    );

    if (
      (response as any)?.data &&
      (response as any)?.success !== false &&
      (response as any).data?.data &&
      (response as any).data?.pagination
    ) {
      return (response as any).data as PaginatedResponse<Company>;
    }
    const maybeItems = (response as any)?.items;
    const maybeTotal = (response as any)?.totalCount;
    if (Array.isArray(maybeItems) && typeof maybeTotal === "number") {
      const totalPages = Math.max(1, Math.ceil(maybeTotal / limit));
      return {
        data: maybeItems as Company[],
        pagination: { page, limit, total: maybeTotal, totalPages },
      };
    }

    const data = (response as any)?.data || response;
    if (Array.isArray(data)) {
      return {
        data: data as Company[],
        pagination: { page, limit, total: data.length, totalPages: 1 },
      };
    }

    throw new Error((response as any)?.message || "Failed to load companies");
  }

  async getById(id: string): Promise<Company> {
    const response = await apiService.get<any>(`/company/${id}`);
    const payload: any = (response as any)?.data ?? response;

    const companyData =
      payload?.data && typeof payload.data === "object"
        ? payload.data
        : typeof payload === "object"
        ? payload
        : null;

    const encryptedId =
      (payload as any)?.encryptedId ??
      (response as any)?.encryptedId ??
      (companyData as any)?.encryptedId ??
      null;

    if (companyData) {
      return { ...companyData, encryptedId } as Company;
    }

    return response as unknown as Company;
  }

  async getByPublicKey(key: string): Promise<Company> {
    const response = await apiService.get<any>(
      `/company/Encrypted/${encodeURIComponent(key)}`
    );
    const payload = (response as any)?.data || response;
    if (payload && typeof payload === "object") {
      if (payload.data) {
        return {
          ...(payload.data as Company),
          encryptedId: payload.encryptedId,
        } as Company;
      }
      return payload as Company;
    }
    return response as unknown as Company;
  }

  async getPackageInfo(id: string): Promise<{ validityMonths?: number; numberOfUsers?: number; packageName?: string }> {
    const response = await apiService.get<any>(`/company/${id}/GetPackageInfo`);
    const payload: any = (response as any)?.data ?? response;
    if (payload && typeof payload === "object") {
      const data = (payload as any).data ?? payload;
      const pkg = data?.package ?? data;
      const numberOfUsers = (data as any)?.numberOfUsers ?? pkg?.numberOfUsers;
      const packageName = (data as any)?.name ?? pkg?.name;
      const details = pkg?.details ?? [];
      const firstDetail = Array.isArray(details) ? details[0] : null;
      const validityDays = firstDetail?.validityDays ?? (pkg as any)?.validityDays;
      const validityMonths =
        validityDays != null ? Math.round(Number(validityDays) / 30) : undefined;
      return { validityMonths, numberOfUsers, packageName };
    }
    return {};
  }

  async create(payload: Partial<Company>): Promise<Company> {
    const response = await apiService.post<Company>(`/company`, payload);
    if ((response as any)?.data && (response as any)?.success !== false) {
      return (response as any).data as Company;
    }
    return response as any as Company;
  }

  async uploadImage(companyId: string, imageData: string): Promise<Company> {
    const response = await apiService.post<Company>(
      `/company/${companyId}/image`,
      {
        imageData,
      }
    );
    if ((response as any)?.data && (response as any)?.success !== false) {
      return (response as any).data as Company;
    }
    return response as any as Company;
  }

  async update(id: string, payload: Partial<Company>): Promise<Company> {
    const response = await apiService.put<Company>(`/company/${id}`, payload);
    if ((response as any)?.data && (response as any)?.success !== false) {
      return (response as any).data as Company;
    }
    return response as any as Company;
  }

  async uploadLicense(id: string, fileName: string, contentBase64: string): Promise<void> {
    const response = await apiService.put(`/company/${id}/license`, {
      fileName,
      content: contentBase64,
    });
    if ((response as any)?.success === false) {
      throw new Error((response as any)?.message || "Subscription upload failed");
    }
  }

  async remove(id: string): Promise<void> {
    const response = await apiService.delete(`/company/${id}`);
    if ((response as any)?.success === false) {
      throw new Error((response as any)?.message || "Delete failed");
    }
  }
}

export const companiesService = new CompaniesService();
export default companiesService;
